/**
 * Icon-Modul — SSOT für alle App-Icons.
 *
 * Redesign S51 (2026-07-18): von hand-gezeichneten SVG-Pfaden auf **Phosphor**
 * (`@phosphor-icons/react`) umgestellt. Grund: die hand-gerollten Pfade + die 48
 * verstreuten Inline-`<svg>` waren „klobig + uneinheitlich" (verschiedene Stroke-
 * Weiten 1.75/2.2/2.4, viewBoxes, Stile). Phosphor gibt library-grade, pixel-
 * konsistente Icons + ein globales Weight (`ICON_WEIGHT`) → ein Regler killt das
 * Klobige. Tree-shaked per-Icon → nur genutzte Icons landen im Bundle.
 *
 * **API unverändert** (`<I.search size={16} className="opacity-60" />`) — die
 * Callsites mussten NICHT angefasst werden; nur die Optik wird besser/einheitlich.
 * Das frühere `stroke`-Prop wird ignoriert (Phosphor nutzt `weight`).
 *
 * Weight global umstellen: `ICON_WEIGHT` unten (`'regular'` → `'light'` = airier).
 *
 * Sweep abgeschlossen (S51, 2026-07-18): die zuvor ~45 verstreuten Inline-`<svg>`
 * im DAM-Client sind auf `I.*` migriert (Transfer-App separat). Verbleibende
 * SVGs sind bewusst kein Icon: `ui/Spinner.tsx` (animierter Lade-Spinner) + die
 * 2 Stroke-Holdouts `dot`/`folder-input` unten.
 */
import type { CSSProperties, FC, ReactNode } from 'react';
import {
  ArrowCounterClockwise,
  ArrowSquareOut,
  Archive,
  Bell,
  Camera,
  CaretDown,
  CaretLeft,
  CaretRight,
  CaretUp,
  ChartLineUp,
  Check,
  CheckCircle,
  Columns,
  Copy,
  CornersOut,
  DotsSix,
  DotsSixVertical,
  DotsThree,
  DotsThreeVertical,
  DownloadSimple,
  Eye,
  File,
  FileText,
  Folder,
  FolderMinus,
  FolderPlus,
  FunnelSimple,
  Gear,
  Globe,
  GridFour,
  House,
  type Icon,
  type IconWeight,
  Image,
  Leaf,
  LinkSimple,
  List,
  ListBullets,
  MagnifyingGlass,
  Minus,
  Package,
  PencilSimple,
  Play,
  Plus,
  Question,
  ShareNetwork,
  SignOut,
  Sparkle,
  Stack,
  Star,
  Tag,
  Television,
  Trash,
  UploadSimple,
  VideoCamera,
  X,
  XCircle,
} from '@phosphor-icons/react';

/** Globales Icon-Weight. `'regular'` (Default) oder `'light'` für einen airier Look. */
const ICON_WEIGHT = 'regular' as const;

type IconProps = {
  size?: number;
  className?: string;
  /** Legacy — von Phosphor ignoriert (dort steuert `weight` die Strichstärke). */
  stroke?: number;
  /** Optionaler per-Icon Weight-Override (Default = ICON_WEIGHT). Für Toggle-
   *  Icons wie den Favoriten-/Cover-Stern: `'fill'` = aktiv, `'regular'` = inaktiv. */
  weight?: IconWeight;
  style?: CSSProperties;
  'aria-hidden'?: boolean | 'true' | 'false';
};

/** Wrapper: behält die bestehende I-API (size/className/style/aria-hidden) über einem
 *  Phosphor-Icon; `stroke` fällt weg, `weight` ist global (ICON_WEIGHT) — per Call
 *  via `weight`-Prop überschreibbar (z.B. `weight="fill"` für aktive Toggles). */
function ph(PhIcon: Icon): FC<IconProps> {
  return ({ size = 16, className, style, weight, ...rest }) => (
    <PhIcon
      size={size}
      weight={weight ?? ICON_WEIGHT}
      className={className}
      style={{ flexShrink: 0, ...style }}
      aria-hidden={rest['aria-hidden'] ?? true}
    />
  );
}

/** Stroke-basierter Holdout für die 2 Icons ohne sauberes Phosphor-Pendant. */
function makeIcon(path: ReactNode): FC<IconProps> {
  return ({ size = 16, className, stroke = 1.75, style, ...rest }) => (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={stroke}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      style={{ flexShrink: 0, ...style }}
      aria-hidden={rest['aria-hidden'] ?? true}
    >
      {path}
    </svg>
  );
}

export const I = {
  home: ph(House),
  search: ph(MagnifyingGlass),
  upload: ph(UploadSimple),
  download: ph(DownloadSimple),
  link: ph(LinkSimple),
  bell: ph(Bell),
  tag: ph(Tag),
  sparkle: ph(Sparkle),
  star: ph(Star),
  chart: ph(ChartLineUp),
  trash: ph(Trash),
  settings: ph(Gear),
  plus: ph(Plus),
  more: ph(DotsThree),
  chevR: ph(CaretRight),
  chevD: ph(CaretDown),
  chevL: ph(CaretLeft),
  close: ph(X),
  maximize: ph(CornersOut),
  play: ph(Play),
  image: ph(Image),
  video: ph(VideoCamera),
  file: ph(File),
  camera: ph(Camera),
  package: ph(Package),
  leaf: ph(Leaf),
  tv: ph(Television),
  globe: ph(Globe),
  grid: ph(GridFour),
  columns: ph(Columns),
  list: ph(ListBullets),
  menu: ph(List),
  stack: ph(Stack),
  folder: ph(Folder),
  'folder-plus': ph(FolderPlus),
  'folder-minus': ph(FolderMinus),
  edit: ph(PencilSimple),
  eye: ph(Eye),
  archive: ph(Archive),
  'x-circle': ph(XCircle),
  'check-circle': ph(CheckCircle),
  check: ph(Check),
  'rotate-ccw': ph(ArrowCounterClockwise),
  help: ph(Question),
  copy: ph(Copy),
  // Sweep S51 (2026-07-18): Keys für die zuletzt verbliebenen hand-gerollten
  // Inline-SVGs (AssetTable-Sort, Share/Logout/Filter/Grip/Minimize …).
  chevU: ph(CaretUp),
  filter: ph(FunnelSimple),
  minus: ph(Minus),
  logout: ph(SignOut),
  share: ph(ShareNetwork),
  open: ph(ArrowSquareOut),
  'file-text': ph(FileText),
  grip: ph(DotsSix),
  'grip-vertical': ph(DotsSixVertical),
  // Holdouts ohne sauberes Phosphor-Pendant:
  dot: makeIcon(<circle cx="12" cy="12" r="3" fill="currentColor" stroke="none" />),
  'folder-input': makeIcon(
    <>
      <path d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
      <path d="M9 14h6m0 0l-2-2m2 2l-2 2" />
    </>,
  ),
} satisfies Record<string, FC<IconProps>>;

export type IconKey = keyof typeof I;

/**
 * Kebab-Menü-Punkte (⋯ vertikal, gefüllt) — Phosphor DotsThreeVertical @ weight fill.
 * Eigenständig (fill statt stroke), geteilt von den Detail-Page-/List-Card-Overflow-Menüs.
 */
export const KebabIcon: FC<{ size?: number; className?: string }> = ({
  size = 18,
  className,
}) => (
  <DotsThreeVertical
    size={size}
    weight="fill"
    className={className}
    style={{ flexShrink: 0 }}
    aria-hidden="true"
  />
);
